import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Calculator, AlertTriangle, TrendingUp, TrendingDown, Loader2, Sparkles,
  Calendar, FileText, Receipt, Info, ChevronRight, Plus, X, Lightbulb, HelpCircle,
} from 'lucide-react';
import HelpInfoDialog from '../components/help/HelpInfoDialog';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid } from 'recharts';

const fmt = (n) => new Intl.NumberFormat('es-ES', { useGrouping: 'always', style: 'currency', currency: 'EUR' }).format(n || 0);
const fmtAxis = (n) => new Intl.NumberFormat('es-ES', { useGrouping: 'always', maximumFractionDigits: 0 }).format(n || 0) + ' €';

const TYPE_META = {
  '303': { color: '#FF6B4A', label: 'IVA', icon: Receipt },
  '130': { color: '#8B5CF6', label: 'IRPF Pago', icon: Calculator },
  '111': { color: '#10B981', label: 'Ret. Prof.', icon: FileText },
  '115': { color: '#F59E0B', label: 'Ret. Alquileres', icon: FileText },
  'irpf_anual': { color: '#3B82F6', label: 'IRPF Anual', icon: Sparkles },
  'is': { color: '#3B82F6', label: 'Soc.', icon: Sparkles },
};

const SEVERITY_CLS = {
  info: 'bg-blue-50 border-blue-200 text-blue-900',
  warning: 'bg-amber-50 border-amber-200 text-amber-900',
  critical: 'bg-red-50 border-red-200 text-red-900',
};

const TaxForecastPage = () => {
  const [loading, setLoading] = useState(true);
  const [current, setCurrent] = useState(null);
  const [timeline, setTimeline] = useState(null);
  const [insights, setInsights] = useState([]);
  const [breakdown, setBreakdown] = useState(null);
  const [showSim, setShowSim] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  const load = useCallback(async () => {
    try {
      const [c, t, i] = await Promise.all([
        api.get('/tax-forecast/current'),
        api.get('/tax-forecast/timeline', { params: { months: 12 } }),
        api.get('/tax-forecast/insights'),
      ]);
      setCurrent(c.data);
      setTimeline(t.data);
      setInsights(i.data?.items || []);
    } catch (e) {
      toast.error('Error cargando predicción fiscal');
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openBreakdown = async (type, year, quarter) => {
    if (type === 'irpf_anual' || type === 'is') {
      toast.info('Desglose anual: ver IRPF anual estimado abajo');
      return;
    }
    try {
      const r = await api.get(`/tax-forecast/breakdown/${type}/${year}/${quarter}`);
      setBreakdown(r.data);
    } catch (e) { toast.error('Error obteniendo desglose'); }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-[#FF6B4A]" /></div>;

  const chartData = (timeline?.items || []).map((it) => ({
    month: new Date(it.due_date).toLocaleDateString('es-ES', { month: 'short', year: '2-digit' }),
    ...it.by_type,
    total: it.total,
  }));

  return (
    <div className="space-y-6" data-testid="tax-forecast-page">
      <div className="flex justify-between items-start flex-wrap gap-3">
        <div>
          <h1 className="text-3xl sm:text-4xl font-heading font-extrabold text-stone-900 flex items-center gap-3">
            <Sparkles className="w-8 h-8 text-[#FF6B4A]" /> Predicción Fiscal
          </h1>
          <p className="text-sm text-stone-500 mt-1">Lo que vas a pagar de impuestos, hoy y al cierre del periodo.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={() => setShowHelp(true)} className="rounded-full border-violet-200 text-violet-700 hover:bg-violet-50" data-testid="taxforecast-help-btn">
            <HelpCircle className="w-4 h-4 mr-1.5" />¿Qué es esto?
          </Button>
          <Button onClick={() => setShowSim(true)} className="bg-[#FF6B4A] text-white" data-testid="open-sim-btn">
            <Plus className="w-4 h-4 mr-1" /> Simulador What-if
          </Button>
        </div>
      </div>

      {/* Disclaimer */}
      <div className="bg-stone-50 border border-stone-200 rounded-lg p-2 text-[11px] text-stone-500 flex gap-2 items-center">
        <Info className="w-3.5 h-3.5 flex-shrink-0" /> {current?.disclaimer || 'Estimaciones orientativas.'}
      </div>

      {/* Insights */}
      {insights.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="tax-insights">
          {insights.map((ins, idx) => (
            <div key={idx} className={`rounded-2xl border p-3 ${SEVERITY_CLS[ins.severity] || SEVERITY_CLS.info}`}>
              <div className="flex items-start gap-2">
                <Lightbulb className="w-5 h-5 flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-sm">{ins.title}</p>
                  <p className="text-xs mt-1 opacity-90">{ins.message}</p>
                  {ins.action_url && (
                    <a href={ins.action_url} className="text-xs underline mt-1 inline-block">{ins.action_label || 'Ver'}</a>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Upcoming due dates */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="upcoming-tax-cards">
        {(current?.upcoming || []).map((it) => {
          const meta = TYPE_META[it.type] || { color: '#777', label: it.type, icon: FileText };
          const Icon = meta.icon;
          return (
            <button
              key={`${it.type}-${it.period_year}-${it.period_quarter}`}
              onClick={() => openBreakdown(it.type, it.period_year, it.period_quarter)}
              className="bg-white rounded-2xl border border-stone-100 p-4 text-left hover:shadow-md hover:border-stone-200 transition"
              data-testid={`tax-card-${it.type}`}
            >
              <div className="flex items-center justify-between mb-2">
                <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-2 py-0.5 rounded-full" style={{ background: meta.color + '20', color: meta.color }}>
                  <Icon className="w-3.5 h-3.5" /> Modelo {it.type}
                </span>
                <span className="text-[10px] text-stone-500 inline-flex items-center gap-1"><Calendar className="w-3 h-3" />{new Date(it.due_date).toLocaleDateString('es-ES', { day: '2-digit', month: 'short' })}</span>
              </div>
              <p className="text-xs text-stone-500">{it.label}</p>
              <p className="text-[10px] text-stone-400 mb-2">{it.period_label} · vence en {it.days_to_due}d</p>
              <p className={`text-2xl font-bold ${it.indicator === 'compensar' ? 'text-emerald-600' : it.indicator === 'sin_pago' ? 'text-stone-500' : 'text-stone-900'}`}>
                {it.indicator === 'compensar' ? '-' : ''}{fmt(it.projected_at_period_end)}
              </p>
              <div className="flex justify-between text-[10px] text-stone-500 mt-2 pt-2 border-t border-stone-100">
                <span>Devengado: <span className="font-mono">{fmt(it.accrued_today)}</span></span>
                <span className="inline-flex items-center text-[#FF6B4A]">Desglose<ChevronRight className="w-3 h-3 ml-0.5" /></span>
              </div>
            </button>
          );
        })}

        {/* Annual estimate */}
        {current?.annual_estimate && (
          <button
            onClick={() => toast.info(`IRPF anual ${current.annual_estimate.year}: ver detalles abajo`)}
            className="bg-gradient-to-br from-blue-50 to-violet-50 rounded-2xl border border-blue-100 p-4 text-left hover:shadow-md transition"
            data-testid="tax-card-annual"
          >
            <div className="flex items-center justify-between mb-2">
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">
                <Sparkles className="w-3.5 h-3.5" /> {current.annual_estimate.type === 'is' ? 'Soc.' : 'IRPF anual'}
              </span>
              <span className="text-[10px] text-blue-700 inline-flex items-center gap-1"><Calendar className="w-3 h-3" />{new Date(current.annual_estimate.due_date).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: '2-digit' })}</span>
            </div>
            <p className="text-xs text-blue-900">{current.annual_estimate.label}</p>
            <p className="text-[10px] text-blue-700 mb-2">{current.annual_estimate.year} estimado</p>
            <p className="text-2xl font-bold text-blue-900">
              {fmt(current.annual_estimate.cuota_a_pagar ?? current.annual_estimate.projected_cuota)}
            </p>
            {current.annual_estimate.tramo_marginal_pct !== undefined && (
              <div className="flex justify-between text-[10px] text-blue-700 mt-2 pt-2 border-t border-blue-100">
                <span>Tramo marginal: <strong>{current.annual_estimate.tramo_marginal_pct}%</strong></span>
                <span>Tipo efectivo: <strong>{current.annual_estimate.tipo_efectivo_pct}%</strong></span>
              </div>
            )}
          </button>
        )}
      </div>

      {/* Annual breakdown panel */}
      {current?.annual_estimate?.type === 'irpf_anual' && (
        <div className="bg-white rounded-2xl border border-stone-100 p-5">
          <h2 className="font-bold text-stone-800 flex items-center gap-2 mb-3"><Calculator className="w-4 h-4 text-[#FF6B4A]" />Desglose IRPF anual estimado {current.annual_estimate.year}</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <Stat label="Ingresos proyectados" value={fmt(current.annual_estimate.projected_income)} sub={`YTD: ${fmt(current.annual_estimate.ytd_income)}`} />
            <Stat label="Gastos proyectados" value={fmt(current.annual_estimate.projected_expenses)} sub={`YTD: ${fmt(current.annual_estimate.ytd_expenses)}`} />
            <Stat label="Rendimiento neto" value={fmt(current.annual_estimate.rendimiento_neto)} sub={`5% gastos ${fmt(current.annual_estimate.reduccion_dificil_justif)}`} />
            <Stat label="Cuota anual" value={fmt(current.annual_estimate.projected_cuota)} sub={`Tramo ${current.annual_estimate.tramo_marginal_pct}%`} highlight />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-3 text-sm">
            <Stat label="Retenciones soportadas" value={fmt(current.annual_estimate.retenciones_soportadas)} />
            <Stat label="A pagar (cuota diferencial)" value={fmt(current.annual_estimate.cuota_a_pagar)} bad={current.annual_estimate.cuota_a_pagar > 0} />
            <Stat label="A devolver" value={fmt(current.annual_estimate.saldo_a_devolver)} good={current.annual_estimate.saldo_a_devolver > 0} />
          </div>
          {current.annual_estimate.siguiente_tramo_desde && (
            <p className="text-xs text-amber-700 mt-3 bg-amber-50 rounded-lg p-2 border border-amber-100">
              <AlertTriangle className="w-3.5 h-3.5 inline mr-1" />
              Siguiente tramo del {current.annual_estimate.siguiente_tramo_pct}% se aplica a partir de {fmt(current.annual_estimate.siguiente_tramo_desde)} de rendimiento.
            </p>
          )}
        </div>
      )}

      {/* Timeline chart */}
      <div className="bg-white rounded-2xl border border-stone-100 p-5" data-testid="tax-timeline">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-bold text-stone-800 flex items-center gap-2"><TrendingUp className="w-4 h-4 text-[#FF6B4A]" />Próximos 12 meses</h2>
          <span className="text-sm text-stone-500">Total estimado: <strong className="text-stone-900">{fmt(timeline?.total || 0)}</strong></span>
        </div>
        {chartData.length === 0 ? (
          <p className="text-center py-8 text-stone-400 text-sm">Sin vencimientos previstos en el horizonte.</p>
        ) : (
          <div style={{ width: '100%', height: 280 }}>
            <ResponsiveContainer>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f5f5f4" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={fmtAxis} />
                <Tooltip formatter={(v) => fmt(v)} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="303" stackId="a" fill={TYPE_META['303'].color} name="IVA 303" />
                <Bar dataKey="130" stackId="a" fill={TYPE_META['130'].color} name="IRPF 130" />
                <Bar dataKey="111" stackId="a" fill={TYPE_META['111'].color} name="Ret. 111" />
                <Bar dataKey="115" stackId="a" fill={TYPE_META['115'].color} name="Ret. 115" />
                <Bar dataKey="irpf_anual" stackId="a" fill={TYPE_META['irpf_anual'].color} name="IRPF anual" />
                <Bar dataKey="is" stackId="a" fill={TYPE_META['is'].color} name="IS" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Breakdown dialog */}
      <BreakdownDialog open={!!breakdown} onClose={() => setBreakdown(null)} data={breakdown} />
      {/* Simulator */}
      <SimulatorDialog open={showSim} onClose={() => setShowSim(false)} />

      {/* Help dialog */}
      <HelpInfoDialog
        open={showHelp}
        onClose={() => setShowHelp(false)}
        icon={Sparkles}
        title="¿Qué es la Predicción Fiscal?"
        intro={(
          <>
            <p><strong>Te dice cuánto vas a pagar de impuestos antes de tener que presentarlos</strong>.
              Calcula al día, sumando tus facturas e ingresos, lo que te toca pagar de IVA (303), IRPF (130/100/180), retenciones (111, 115) e incluso el IRPF anual (renta).</p>
            <p className="mt-2">Así nunca te pilla por sorpresa una liquidación y puedes <strong>provisionar el dinero a tiempo</strong>.</p>
          </>
        )}
        sections={[
          {
            icon: Calculator, iconColor: '#FF6B4A', title: 'Modelos que predice',
            body: [
              '<strong>Modelo 303</strong>: IVA trimestral (lo que ingresas vs lo que pagas en compras)',
              '<strong>Modelo 130</strong>: pago fraccionado IRPF (autónomos estimación directa)',
              '<strong>Modelo 111</strong>: retenciones a profesionales en tus gastos',
              '<strong>Modelo 115</strong>: retenciones de alquileres pagados',
              '<strong>IRPF anual (Renta)</strong>: estimación de lo que pagarás en junio',
              '<strong>Impuesto de Sociedades</strong> (si eres SL)',
            ],
          },
          {
            icon: Lightbulb, iconColor: '#F59E0B', title: 'Insights automáticos',
            body: 'Detecta situaciones tipo: "vas a pagar 3.200 € de IVA este trimestre, mete 1.066 €/mes a una cuenta aparte", o "este mes tus gastos deducibles bajaron — revisa si te falta algo por escanear".',
          },
          {
            icon: Sparkles, iconColor: '#7C3AED', title: 'Simulador What-if',
            body: [
              '"<strong>¿Y si facturo 5.000 € más este trimestre?</strong>" → te dice cuánto pagarás de IVA',
              '"<strong>¿Y si compro un coche de 25.000 €?</strong>" → calcula el ahorro fiscal',
              '"<strong>¿Y si meto un gasto de 800 €?</strong>" → ves el impacto exacto',
            ],
          },
          {
            icon: Calendar, iconColor: '#10B981', title: 'Calendario fiscal',
            body: 'Muestra las próximas fechas límite (20 de abril, 20 de julio, 20 de octubre, 30 de enero) con cuánto vas a tener que pagar en cada una.',
          },
        ]}
      />
    </div>
  );
};

const Stat = ({ label, value, sub, highlight, good, bad }) => (
  <div className={`rounded-xl p-3 ${highlight ? 'bg-stone-900 text-white' : 'bg-stone-50'}`}>
    <p className={`text-[10px] uppercase font-bold ${highlight ? 'text-stone-300' : 'text-stone-500'}`}>{label}</p>
    <p className={`text-xl font-bold mt-1 ${good ? 'text-emerald-600' : bad ? 'text-rose-600' : ''}`}>{value}</p>
    {sub && <p className={`text-[10px] mt-0.5 ${highlight ? 'text-stone-400' : 'text-stone-500'}`}>{sub}</p>}
  </div>
);

const BreakdownDialog = ({ open, onClose, data }) => {
  if (!data) return null;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto" data-testid="breakdown-dialog">
        <DialogHeader><DialogTitle>Desglose Modelo {data.type} · {data.quarter}T {data.year}</DialogTitle></DialogHeader>
        <div className="space-y-4 text-sm">
          <div className="bg-stone-50 rounded-xl p-3">
            <p className="text-xs uppercase font-bold text-stone-500 mb-2">Resultado del cálculo</p>
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(data.calculation).filter(([k]) => !k.startsWith('vat_groups')).slice(0, 10).map(([k, v]) => (
                <div key={k} className="flex justify-between text-xs"><span className="text-stone-500">{k}:</span><span className="font-mono">{typeof v === 'number' ? fmt(v) : String(v).slice(0, 30)}</span></div>
              ))}
            </div>
          </div>
          {data.invoices?.length > 0 && (
            <div>
              <h3 className="font-bold mb-2 text-sm">{data.invoices.length} factura(s) emitidas</h3>
              <table className="w-full text-xs">
                <thead className="bg-stone-50"><tr><th className="text-left px-2 py-1">Nº</th><th className="text-left px-2 py-1">Cliente</th><th className="text-left px-2 py-1">Fecha</th><th className="text-right px-2 py-1">Base</th><th className="text-right px-2 py-1">IVA</th></tr></thead>
                <tbody>{data.invoices.slice(0, 50).map((i) => (
                  <tr key={i.id} className="border-t border-stone-100"><td className="px-2 py-1 font-mono">{i.invoice_number}</td><td className="px-2 py-1">{i.client_name}</td><td className="px-2 py-1">{i.issue_date}</td><td className="px-2 py-1 text-right">{fmt(i.subtotal)}</td><td className="px-2 py-1 text-right">{fmt(i.tax_amount)}</td></tr>
                ))}</tbody>
              </table>
            </div>
          )}
          {data.expenses?.length > 0 && (
            <div>
              <h3 className="font-bold mb-2 text-sm">{data.expenses.length} gasto(s)</h3>
              <table className="w-full text-xs">
                <thead className="bg-stone-50"><tr><th className="text-left px-2 py-1">Proveedor</th><th className="text-left px-2 py-1">Categoría</th><th className="text-left px-2 py-1">Fecha</th><th className="text-right px-2 py-1">Base</th><th className="text-right px-2 py-1">IVA</th></tr></thead>
                <tbody>{data.expenses.slice(0, 50).map((e) => (
                  <tr key={e.id} className="border-t border-stone-100"><td className="px-2 py-1">{e.vendor}</td><td className="px-2 py-1 text-stone-500">{e.category}</td><td className="px-2 py-1">{e.expense_date}</td><td className="px-2 py-1 text-right">{fmt(e.amount)}</td><td className="px-2 py-1 text-right">{fmt(e.tax_amount)}</td></tr>
                ))}</tbody>
              </table>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const SimulatorDialog = ({ open, onClose }) => {
  const [invForm, setInvForm] = useState({ subtotal: '', tax_rate: '21', issue_date: new Date().toISOString().slice(0, 10) });
  const [expForm, setExpForm] = useState({ amount: '', tax_amount: '', expense_date: new Date().toISOString().slice(0, 10) });
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const run = async () => {
    const payload = { hypothetical_invoices: [], hypothetical_expenses: [] };
    if (invForm.subtotal) payload.hypothetical_invoices.push({ subtotal: parseFloat(invForm.subtotal), tax_rate: parseFloat(invForm.tax_rate), issue_date: invForm.issue_date });
    if (expForm.amount) payload.hypothetical_expenses.push({ amount: parseFloat(expForm.amount), tax_amount: parseFloat(expForm.tax_amount || (expForm.amount * 0.21)), expense_date: expForm.expense_date });
    if (!payload.hypothetical_invoices.length && !payload.hypothetical_expenses.length) {
      return toast.error('Añade al menos una factura o gasto hipotético');
    }
    setBusy(true);
    try {
      const r = await api.post('/tax-forecast/simulate', payload);
      setResult(r.data);
    } catch (e) { toast.error('Error simulando'); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && (onClose() || setResult(null))}>
      <DialogContent className="max-w-2xl" data-testid="simulator-dialog">
        <DialogHeader><DialogTitle>Simulador What-if fiscal</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="bg-stone-50 rounded-xl p-3 space-y-2">
              <p className="text-xs uppercase font-bold text-stone-500">Factura hipotética</p>
              <Input type="number" placeholder="Base 5000" value={invForm.subtotal} onChange={(e) => setInvForm({ ...invForm, subtotal: e.target.value })} data-testid="sim-inv-subtotal" />
              <div className="grid grid-cols-2 gap-2">
                <Input type="number" placeholder="IVA % (21)" value={invForm.tax_rate} onChange={(e) => setInvForm({ ...invForm, tax_rate: e.target.value })} />
                <Input type="date" value={invForm.issue_date} onChange={(e) => setInvForm({ ...invForm, issue_date: e.target.value })} />
              </div>
            </div>
            <div className="bg-stone-50 rounded-xl p-3 space-y-2">
              <p className="text-xs uppercase font-bold text-stone-500">Gasto hipotético</p>
              <Input type="number" placeholder="Base 1000" value={expForm.amount} onChange={(e) => setExpForm({ ...expForm, amount: e.target.value })} data-testid="sim-exp-amount" />
              <div className="grid grid-cols-2 gap-2">
                <Input type="number" placeholder="IVA (210)" value={expForm.tax_amount} onChange={(e) => setExpForm({ ...expForm, tax_amount: e.target.value })} />
                <Input type="date" value={expForm.expense_date} onChange={(e) => setExpForm({ ...expForm, expense_date: e.target.value })} />
              </div>
            </div>
          </div>
          <Button onClick={run} className="w-full bg-[#FF6B4A] text-white" disabled={busy} data-testid="sim-run-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Sparkles className="w-4 h-4 mr-2" />} Calcular delta
          </Button>
          {result && (
            <div className="bg-stone-900 text-white rounded-xl p-4 space-y-2" data-testid="sim-results">
              <p className="text-sm font-bold">Impacto fiscal</p>
              <div className="grid grid-cols-3 gap-2 text-xs">
                <DeltaCol label="IVA 303" base={result.baseline.iva_303} sim={result.simulated.iva_303} delta={result.delta.iva_303} />
                <DeltaCol label="IRPF 130" base={result.baseline.irpf_130} sim={result.simulated.irpf_130} delta={result.delta.irpf_130} />
                <DeltaCol label="IRPF anual" base={result.baseline.annual_cuota} sim={result.simulated.annual_cuota} delta={result.delta.annual_cuota} />
              </div>
              <div className="pt-2 border-t border-stone-700">
                <div className="flex justify-between font-bold">
                  <span>Total extra a pagar este año:</span>
                  <span className={result.total_extra_pay >= 0 ? 'text-rose-400' : 'text-emerald-400'}>{fmt(result.total_extra_pay)}</span>
                </div>
                {result.delta.tramo_changed && (
                  <p className="text-xs text-amber-300 mt-1"><AlertTriangle className="w-3 h-3 inline mr-1" />Has saltado de tramo IRPF.</p>
                )}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const DeltaCol = ({ label, base, sim, delta }) => {
  if (base === null || base === undefined) return <div className="opacity-40"><p className="text-stone-400">{label}</p><p>—</p></div>;
  const positive = (delta || 0) > 0;
  return (
    <div>
      <p className="text-stone-400">{label}</p>
      <p className="text-stone-300">{fmt(base)} → {fmt(sim)}</p>
      <p className={`font-bold ${positive ? 'text-rose-400' : delta < 0 ? 'text-emerald-400' : 'text-stone-500'}`}>
        {positive ? '+' : ''}{fmt(delta)}
      </p>
    </div>
  );
};

export default TaxForecastPage;
